import { computed, ref, watch } from 'vue'
import type { Ref } from 'vue'
import { useStore } from 'vuex'
import {
  getLockedAssemblySelectionRedirects,
  getOrderableLockedAssemblyParts
} from '@/helpers/threeDLockedAssembly'
import type {
  Part,
  ThreeDViewerOrderableAssemblyPart,
  ThreeDViewerPartSearchSource
} from '@/interfaces/library'

export type ThreeDAssemblyOrderingViewerApi = {
  clearLibraryAssemblyOrderingFocus?: () => boolean
  selectElements?: (nodeIds: string[]) => void
  setLibraryAssemblyOrderingFocus?: (elementIds: number[]) => boolean
}

type SelectedViewerElement = { id: string } | null

type UseThreeDLockedAssemblyOrderingOptions = {
  cardOpen: Ref<boolean>
  mappedPart: Ref<Part | null>
  partSearchSource: Ref<ThreeDViewerPartSearchSource[]>
  selectedElement: Ref<SelectedViewerElement>
  snapshotLockedAssemblyParentIds: Ref<Record<number, number>>
  viewer: Ref<ThreeDAssemblyOrderingViewerApi | null>
  viewerActiveContent: Ref<string>
}

export function useThreeDLockedAssemblyOrdering ({
  cardOpen,
  mappedPart,
  partSearchSource,
  selectedElement,
  snapshotLockedAssemblyParentIds,
  viewer,
  viewerActiveContent
}: UseThreeDLockedAssemblyOrderingOptions) {
  const store = useStore()
  const orderingModeActive = ref(false)
  const selectedPartKey = ref<string | null>(null)
  const childPricing = ref<Record<string, ThreeDViewerOrderableAssemblyPart>>({})
  let pricingRequestId = 0

  const allSelectionRedirects = computed(() => ({
    ...getLockedAssemblySelectionRedirects(partSearchSource.value),
    ...snapshotLockedAssemblyParentIds.value
  }))
  const selectedAssemblyId = computed(() => {
    const dbId = Number(selectedElement.value?.id)
    if (!Number.isInteger(dbId)) return null

    return Object.values(allSelectionRedirects.value).includes(dbId) ? dbId : null
  })
  const partSources = computed(() => {
    const assemblyDbId = selectedAssemblyId.value
    if (assemblyDbId === null) return []

    return getOrderableLockedAssemblyParts(
      assemblyDbId,
      partSearchSource.value,
      allSelectionRedirects.value
    )
  })
  const parts = computed(() =>
    partSources.value.map(item => childPricing.value[item.key] ?? item)
  )
  const elementIds = computed(() => partSources.value.flatMap(item => item.elementIds))
  const selectionRedirects = computed(() => {
    if (!orderingModeActive.value) return allSelectionRedirects.value

    const selectableElementIds = new Set(elementIds.value)
    return Object.fromEntries(
      Object.entries(allSelectionRedirects.value)
        .filter(([childId]) => !selectableElementIds.has(Number(childId)))
    )
  })
  const visibleAssemblyPartNumber = computed(() => {
    const part = mappedPart.value as (Part & {
      partNumberVisible?: boolean
      visible?: boolean
    }) | null
    return part?.visible !== false && part?.partNumberVisible !== false
      ? part?.partNumber ?? null
      : null
  })

  async function loadChildPricing (
    item: ThreeDViewerOrderableAssemblyPart,
    requestId: number
  ) {
    let part = item.part
    try {
      const pricing = await store.dispatch('content/getPricingWithReturn', {
        ...part,
        mediaIdentifier: store.state.content.toc?.book?.identifier ?? '',
        pricing: part,
        type: 'part'
      })
      part = { ...part, ...pricing }
    } catch {
      // Pricing is optional enrichment; retain static Part pricing and cart access.
    }

    if (requestId !== pricingRequestId) return
    childPricing.value = {
      ...childPricing.value,
      [item.key]: { ...item, part, pricingLoaded: true }
    }
  }

  function refreshChildPricing () {
    const requestId = ++pricingRequestId
    if (
      viewerActiveContent.value !== '3d' ||
      !cardOpen.value ||
      partSources.value.length === 0
    ) {
      childPricing.value = {}
      return
    }

    childPricing.value = Object.fromEntries(partSources.value.map(item => [item.key, item]))
    partSources.value.forEach(item => loadChildPricing(item, requestId))
  }

  function clearMode (restoreAssemblySelection = false) {
    if (!orderingModeActive.value) return

    const assemblyDbId = selectedAssemblyId.value
    viewer.value?.clearLibraryAssemblyOrderingFocus?.()
    orderingModeActive.value = false
    selectedPartKey.value = null
    if (restoreAssemblySelection && assemblyDbId !== null) {
      viewer.value?.selectElements?.([String(assemblyDbId)])
    }
  }

  function activateMode () {
    if (orderingModeActive.value || elementIds.value.length === 0) return

    if (viewer.value?.setLibraryAssemblyOrderingFocus?.(elementIds.value)) {
      orderingModeActive.value = true
      selectedPartKey.value = null
    }
  }

  function selectPart (item: ThreeDViewerOrderableAssemblyPart) {
    if (!orderingModeActive.value) return

    selectedPartKey.value = item.key
    viewer.value?.selectElements?.(item.elementIds.map(String))
  }

  function handleDirectSelection (selectedDbId?: number) {
    if (!orderingModeActive.value) return false

    const childPart = parts.value.find(({ elementIds: childElementIds }) =>
      selectedDbId !== undefined && childElementIds.includes(selectedDbId)
    )
    if (!childPart) {
      clearMode()
      return false
    }

    selectedPartKey.value = childPart.key
    return true
  }

  function reset () {
    clearMode()
    pricingRequestId++
    childPricing.value = {}
  }

  watch(
    [
      cardOpen,
      selectedAssemblyId,
      viewerActiveContent,
      () => partSources.value.map(({ key }) => key).join('|')
    ],
    refreshChildPricing
  )
  watch(viewerActiveContent, (activeContent) => {
    if (activeContent !== '3d') clearMode(true)
  })

  return {
    activateMode,
    clearMode,
    handleDirectSelection,
    orderingModeActive,
    parts,
    reset,
    selectedPartKey,
    selectionRedirects,
    selectPart,
    visibleAssemblyPartNumber
  }
}
